import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { FilterSelect } from '../../components/filters/FilterSelect';
import { PageHeader } from '../../components/PageHeader';
import { Pill } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listCategories, listProductsPage } from '../../data/fetchers';
import { deleteProduct } from '../../data/mutators';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';

interface ProductRow {
  id: string;
  name: string;
  category: string;
  baseUnit: string;
  active: boolean;
}

const PAGE_SIZE = 50;

export function ProductListView() {
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditMasterData(user) : false;
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(1);
  const qc = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products-page'] });
    },
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const trimmedSearch = search.trim();
  const { data, isPending } = useQuery({
    queryKey: ['products-page', page, trimmedSearch, categoryId, status],
    queryFn: () =>
      listProductsPage({
        page,
        pageSize: PAGE_SIZE,
        search: trimmedSearch || undefined,
        categoryId: categoryId || undefined,
        status,
      }),
  });
  useEffect(() => {
    setPage(1);
  }, [trimmedSearch, categoryId, status]);

  const rows = useMemo<ProductRow[]>(() => {
    return (data?.items ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      category: p.categoryName ?? p.categoryId ?? '-',
      baseUnit: p.baseUnitName ?? '-',
      active: p.active,
    }));
  }, [data?.items]);

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (!isPending && page > lastPage) {
      setPage(lastPage);
    }
  }, [isPending, lastPage, page]);

  const columns = useMemo<ColumnDef<ProductRow, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'ชื่อสินค้า' },
      { accessorKey: 'category', header: 'หมวดหมู่' },
      { accessorKey: 'baseUnit', header: 'หน่วยฐาน' },
      {
        accessorKey: 'active',
        header: 'สถานะ',
        cell: (c) =>
          c.getValue<boolean>() ? (
            <Pill tone="ok">ใช้งาน</Pill>
          ) : (
            <Pill tone="neutral">ปิด</Pill>
          ),
      },
      {
        id: 'go',
        header: '',
        cell: (c) => (
          <div className="flex items-center gap-3 justify-end">
            <button
              className="text-primary text-sm-label"
              onClick={() => navigate(`/products/${c.row.original.id}`)}
            >
              {canEdit ? 'แก้ไข' : 'ดู'}
            </button>
            {canEdit && c.row.original.active && (
              <button
                className="text-danger text-sm-label"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `ลบสินค้า “${c.row.original.name}” ใช่หรือไม่? สินค้าจะถูกปิดใช้งานและไม่หายจากประวัติเดิม`,
                    )
                  ) {
                    deleteMutation.mutate(c.row.original.id);
                  }
                }}
              >
                ลบ
              </button>
            )}
          </div>
        ),
      },
    ],
    [navigate, canEdit],
  );

  return (
    <div className="bo-fade">
      <PageHeader
        title="รายการสินค้า"
        subtitle={`${total} รายการ`}
        actions={
          canEdit ? (
            <>
              <Btn kind="default" onClick={() => navigate('/products/import')}>
                นำเข้า Excel
              </Btn>
              <Btn kind="primary" onClick={() => navigate('/products/new')}>
                + เพิ่มสินค้า
              </Btn>
            </>
          ) : undefined
        }
      />
      <div className="mb-4 grid gap-3 md:grid-cols-[minmax(220px,1fr)_220px_180px]">
        <label className="space-y-1">
          <span className="text-sm-label text-ink-3">ค้นหาสินค้า/บาร์โค้ด</span>
          <input
            className="bo-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="เช่น โค้ก หรือ 885..."
            aria-label="ค้นหาสินค้า"
          />
        </label>
        <FilterSelect
          label="หมวดหมู่"
          value={categoryId}
          onChange={setCategoryId}
          options={[
            { value: '', label: 'ทุกหมวดหมู่' },
            ...categories.map((category) => ({
              value: category.id,
              label: category.name,
            })),
          ]}
        />
        <FilterSelect
          label="สถานะ"
          value={status}
          onChange={(value) => setStatus(value as typeof status)}
          options={[
            { value: 'all', label: 'ทั้งหมด' },
            { value: 'active', label: 'ใช้งาน' },
            { value: 'inactive', label: 'ปิดใช้งาน' },
          ]}
        />
      </div>
      {isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-10 rounded-card bg-line-2 animate-pulse"
            />
          ))}
        </div>
      ) : (
        <>
          <DataTable columns={columns} data={rows} pageSize={PAGE_SIZE} />
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm-label text-ink-3">
              หน้า {page} / {lastPage}
            </span>
            <div className="flex gap-2">
              <Btn
                kind="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ก่อนหน้า
              </Btn>
              <Btn
                kind="ghost"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              >
                ถัดไป
              </Btn>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
